import React from 'react';

export default function TermsOfService() {
  return (
    <div className="max-w-3xl mx-auto p-6 pt-24 space-y-6 text-gray-800">
      <h1 className="text-3xl font-bold">Terms of Service</h1>
      <p className="text-xl">LettuceTalk</p>
      <p className="text-sm text-gray-500">Last Updated: May 28, 2025</p>

      {/* 1. Definitions */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">1. Definitions</h2>
        <p>
          <strong>“Services”</strong> means LettuceTalk’s website, applications, AI Card Generator, subscriptions, and physical card decks.<br />
          <strong>“We”</strong>, <strong>“Us”</strong> and <strong>“Impact Moonshots”</strong> refer to Creashell LLC (dba Impact Moonshots).<br />
          <strong>“You”</strong> refers to any user of the Services.
        </p>
      </section>

      {/* 2. Acceptance of Terms */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">2. Acceptance of Terms</h2>
        <p>
          These Terms of Service (“Terms”) govern your access to and use of the Services. By accessing or using any portion of the Services, you agree to be bound by these Terms, our <a href="/privacy" className="underline">Privacy Policy</a>, and any other posted guidelines.
        </p>
        <p>
          You represent that you are at least 18 years old and capable of entering into a binding contract.
        </p>
      </section>

      {/* 3. License Grant & Restrictions */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">3. License Grant & Restrictions</h2>
        <p>
          Subject to your compliance with these Terms, Impact Moonshots grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for personal, non-commercial use only. You may not:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Reverse-engineer, decompile, disassemble, or otherwise attempt to derive any source code or underlying ideas;</li>
          <li>Modify, distribute, sublicense, or create derivative works based on the Services;</li>
          <li>Use the Services to store or transmit harmful code or malicious content;</li>
          <li>Circumvent any usage restrictions or remove proprietary notices.</li>
        </ul>
      </section>

      {/* 4. Description of Services */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">4. Description of Services</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>AI Card Generator:</strong> An LLM-powered tool for personal, non-commercial inspiration only — not professional advice.</li>
          <li><strong>Physical Card Decks:</strong> One-time purchases of hand-designed decks, shipped to U.S. addresses only.</li>
        </ul>
      </section>

      {/* 5. Orders, Billing & Cancellation */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">5. Orders, Billing & Cancellation</h2>
        <p>
          Deck purchases are charged in USD via our secure payment link. Orders are not binding until payment is confirmed. Available to U.S. addresses only; orders from other jurisdictions will be rejected.
        </p>
      </section>

      {/* 6. Shipping & Returns */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">6. Shipping & Returns</h2>
        <h3 className="text-xl font-medium">Shipping</h3>
        <p>We ship to U.S. addresses only. Delivery estimates are non-binding; we are not responsible for carrier delays or loss.</p>
        <h3 className="text-xl font-medium">Returns & Refunds</h3>
        <p>Defective or damaged decks may be returned within 14 days. Email care@imoonshots.com with your order number and photo. At our discretion, we will replace or refund within 7 business days.</p>
      </section>

      {/* 7. Disclaimers & Limitation of Liability */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">7. Disclaimers & Limitation of Liability</h2>
        <p>
          THE SERVICES ARE PROVIDED “AS IS.” IMPACT MOONSHOTS DISCLAIMS ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.<br />
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, IMPACT MOONSHOTS SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. OUR AGGREGATE LIABILITY IS LIMITED TO THE AMOUNT YOU PAID US IN THE SIX MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
        </p>
      </section>

      {/* 8. Indemnification */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">8. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Impact Moonshots and its affiliates from any claims, damages, or expenses (including reasonable attorneys’ fees) arising from your breach of these Terms or your use of the Services.
        </p>
      </section>

      {/* 9. DMCA Notice */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">9. DMCA Notice</h2>
        <p>
          If you believe any content on the Services infringes your copyright, please provide our designated agent at care@imoonshots.com with a written notice including: (a) identification of the copyrighted work, (b) the infringing material’s location, (c) your contact information, and (d) a statement of good faith belief.
        </p>
      </section>

      {/* 10. Assignment & Change of Control */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">10. Assignment & Change of Control</h2>
        <p>
          Impact Moonshots may assign its rights and obligations under these Terms (including in connection with a merger, acquisition, or asset sale). You may not assign your rights or delegate your obligations without our prior written consent. These Terms bind and benefit our respective successors and permitted assigns.
        </p>
      </section>

      {/* 11. Governing Law & Dispute Resolution */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">11. Governing Law & Dispute Resolution</h2>
        <p>
          These Terms are governed by California law. You and Impact Moonshots submit to the exclusive jurisdiction of the Santa Clara County courts.
        </p>
      </section>

      {/* 12. Survivability */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">12. Survivability</h2>
        <p>
          Provisions regarding Disclaimers, Limitation of Liability, Indemnification, Assignment, Change of Control, and Governing Law survive termination of these Terms.
        </p>
      </section>

      {/* 13. Changes to Terms */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">13. Changes to Terms</h2>
        <p>
          We may update these Terms by posting an updated version here. Continued use constitutes acceptance.
        </p>
      </section>

      {/* 14. Contact & Accessibility */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">14. Contact & Accessibility</h2>
        <p>
          For questions about these Terms or assistance accessing any part of the Services, email care@imoonshots.com.
        </p>
      </section>

      {/* 15. Accessibility */}
      <section className="space-y-2">
        <h2 className="text-2xl font-semibold">15. Accessibility</h2>
        <p>
          If you need assistance accessing any part of the Services due to a disability, please contact us at care@imoonshots.com and we will make reasonable efforts to accommodate your needs.
        </p>
      </section>

      <p className="text-center text-gray-500">© 2025 Impact Moonshots. All rights reserved.</p>
    </div>
  );
}
